import React, { createContext, useState, useEffect, useCallback } from 'react';
import { API } from "../api/config";

export const AdminContext = createContext();

export const AdminProvider = ({ children }) => {
    const [isAdmin, setIsAdmin] = useState(false);
    const [adminLoading, setAdminLoading] = useState(true);

    // Backend Base URL
    const API_BASE_URL = API.LOGIN;

    // Validate the token against the backend
    const validateToken = useCallback(async (token, retries = 1) => {
        try {
            // Note: Adjust the path to /validate or /validate-token based on your exact backend setup
            const response = await fetch(`${API_BASE_URL}/auth/users/validate-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ token })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success || data.valid) {
                    setIsAdmin(true);
                } else {
                    throw new Error('Token rejeitado pelo servidor');
                }
            } else if (response.status === 401 || response.status === 403) {
                // Explicitly unauthorized/expired
                throw new Error('Sessão expirada ou não autorizada');
            } else {
                throw new Error('Falha na rede ou servidor');
            }
        } catch (error) {
            console.warn('[Admin Auth]', error.message);
            
            // Retry once on network or 500 errors (not on 401s)
            if (retries > 0 && error.message === 'Falha na rede ou servidor') {
                console.log('[Admin Auth] Tentando reconectar...');
                return validateToken(token, retries - 1);
            }

            // Validation failed, clean up
            localStorage.removeItem('admin_token');
            setIsAdmin(false);
        } finally {
            setAdminLoading(false);
        }
    }, []);

    // Check session on app load
    useEffect(() => {
        const token = localStorage.getItem('admin_token');
        if (token) {
            validateToken(token);
        } else {
            setAdminLoading(false);
            setIsAdmin(false);
        }
    }, [validateToken]);

    // Handle Admin Login
    const adminLogin = async (nome, senha) => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/users/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ nome, senha })
            });

            if (!response.ok) {
                throw new Error('Credenciais inválidas');
            }

            const data = await response.json();
            
            if (data.success && data.token) {
                // Store ONLY the secure token
                localStorage.setItem('admin_token', data.token);
                setIsAdmin(true);
                return { success: true };
            } else {
                throw new Error('Token não retornado pelo servidor');
            }
            
        } catch (error) {
            console.error('Erro no login admin:', error);
            return { success: false, message: error.message || 'Erro ao conectar ao servidor.' };
        }
    };

    // Handle Admin Logout
    const adminLogout = () => {
        localStorage.removeItem('admin_token');
        setIsAdmin(false);
    };

    return (
        <AdminContext.Provider value={{ isAdmin, adminLoading, adminLogin, adminLogout }}>
            {children}
        </AdminContext.Provider>
    );
};